import { Card } from "@/components/ui/card";
import { Brain, Code, Database, Lightbulb, Rocket, Users } from "lucide-react";
import { useScrollReveal, useStaggeredReveal } from "@/hooks/useScrollReveal";
import { use3DTilt, useSpotlight } from "@/hooks/use3DTilt";

const spiliosSpiliopoulos = "/images/spilios-spiliopoulos.jpg";

const skills = [
  {
    icon: Brain,
    title: "AI Systems & Agents",
    description: "Voice Agents (Retell AI), Chat Agents, SDR Agents, OpenAI API",
    size: "bento-item-wide"
  },
  {
    icon: Code,
    title: "Automation & Integration",
    description: "n8n Workflows, GoHighLevel CRM, Twilio, Supabase, GCP",
    size: ""
  },
  {
    icon: Database,
    title: "Lead Generation Systems",
    description: "LinkedIn Sales Navigator, HeyReach, Instantly, Smartlead",
    size: ""
  },
  {
    icon: Lightbulb,
    title: "Research & ML",
    description: "Deep Learning, Computer Vision, Medical Imaging AI",
    size: ""
  },
  {
    icon: Rocket,
    title: "Technology Strategy",
    description: "Software Architecture, System Design, Technical Planning",
    size: ""
  },
  {
    icon: Users,
    title: "Leadership & Teaching",
    description: "CTO Experience, 75+ Students Taught, 93.33% Success Rate",
    size: "bento-item-wide"
  }
];

const SkillCard = ({ skill, index, isVisible }: { skill: typeof skills[0], index: number, isVisible: boolean }) => {
  const tilt = use3DTilt(10);
  const spotlight = useSpotlight();

  return (
    <div
      ref={tilt.ref}
      onMouseMove={(e) => {
        tilt.onMouseMove(e);
        spotlight.onMouseMove(e);
      }}
      onMouseLeave={tilt.onMouseLeave}
      className={`${skill.size} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{
        transitionDelay: `${index * 100}ms`,
        transition: 'opacity 0.5s ease, transform 0.5s ease'
      }}
    >
      <Card
        ref={spotlight.ref}
        className="h-full p-6 card-enhanced spotlight cursor-default group relative overflow-hidden"
      >
        <div className="relative z-10">
          <div className="relative mb-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
              <skill.icon className="w-6 h-6 text-primary" />
            </div>
          </div>
          <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">
            {skill.title}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">{skill.description}</p>
        </div>
      </Card>
    </div>
  );
};

const About = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: cardRef, isVisible: cardVisible } = useScrollReveal({ threshold: 0.1 });
  const { containerRef: skillsRef, visibleItems } = useStaggeredReveal(skills.length, 100);
  const mainCardTilt = use3DTilt(5);

  return (
    <section id="about" className="py-32 px-6 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Floating decorative elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent/5 rounded-full blur-3xl" />

      <div className="container max-w-6xl mx-auto relative z-10">
        <div
          ref={headerRef}
          className={`mb-20 transition-all duration-1000 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14 max-w-5xl mx-auto">
            {/* Profile Image */}
            <div className="relative group flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-indigo-600/30 rounded-full blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
              <img
                src={spiliosSpiliopoulos}
                alt="Spilios Spiliopoulos - AI/ML Systems & Architecture"
                className="relative rounded-full w-48 h-48 md:w-56 md:h-56 object-cover object-[center_20%] shadow-2xl border-4 border-primary/30 group-hover:scale-105 group-hover:border-primary/50 transition-all duration-500"
              />
            </div>

            {/* Heading + bio */}
            <div className="space-y-6 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-bold text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.7),0_0_60px_rgba(139,92,246,0.4)]">
                About Me
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                26 years old, based in Ioannina, Greece. Master's in Computer Science Engineering with Machine Learning
                specialization from University of Ioannina. CTO & AI Architect building production-grade AI & automations systems.
              </p>
            </div>
          </div>
        </div>

        {/* Company Cards */}
        <div
          ref={cardRef}
          className={`relative mb-20 transition-all duration-1000 delay-200 ${
            cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div
            ref={mainCardTilt.ref}
            onMouseMove={mainCardTilt.onMouseMove}
            onMouseLeave={mainCardTilt.onMouseLeave}
            className="card-3d"
          >
            <Card className="card-3d-inner p-8 md:p-12 card-enhanced border-primary/10 relative overflow-hidden">
              <div className="relative z-10 max-w-4xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12">
                  <div className="space-y-4 group">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                        <Rocket className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                          ScaleLogix AI Consulting
                        </h3>
                        <p className="text-sm text-muted-foreground">CTO & AI Architect</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Overseeing technology strategy, software architecture, and AI systems
                      implementation. Building production-grade AI & automations systems.
                    </p>
                  </div>
                  <div className="space-y-4 group">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                        <Lightbulb className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                          sAIve time
                        </h3>
                        <p className="text-sm text-muted-foreground">Co-Founder</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Co-founded with Leo Tsandarliotis. Offering AI Automation Bundles with custom agents,
                      n8n workflows, CRM integrations, and a Productized Lead Generation System.
                    </p>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-border/50">
                  <p className="text-center text-lg text-muted-foreground italic">
                    "The best AI system is the one you forget is AI. My specialty is integrating AI into production
                    systems that deliver measurable business impact. <span className="text-primary font-medium">Simpler solutions usually win.</span>"
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Skills Bento Grid */}
        <div ref={skillsRef} className="bento-grid">
          {skills.map((skill, index) => (
            <SkillCard
              key={index}
              skill={skill}
              index={index}
              isVisible={visibleItems[index]}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
